<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
  <div class="blog-post top-[4.7rem] relative md:top-auto">
    <!-- Breadcrumb -->
    <section
      class="px-5 lg:px-0 md:px-0 w-full md:container overflow-auto whitespace-nowrap breadcrumbs-container border-manatee border-solid block md:hidden"
    >
      <Breadcrumbs :navigation="navigation" />
    </section>
    <!-- ./Breadcrumb -->

    <!-- Datepicker -->
    <div v-if="post.acf && post.acf.enable_cvr" class="bg-blue-600 md:pb-5">
      <div class="md:container p-5 md:p-0">
        <div class="flex items-center">
          <span
            class="text-lg md:text-3xl airport-title-main flex mt-8 mb-0 md:mb-2.5 md:mt-3 text-white font-cocogoose relative w-full lg:w-7/10 sm:w-auto"
          >
            {{ maintitle }}
          </span>
        </div>
        <DatePicker :show-airports="post.acf.airport_slug && !post.acf.airport_slug.length" />
      </div>
    </div>
    <!-- ./Datepicker -->

    <!-- Featured Image -->
    <section v-else class="blog-post__featured-image">
      <img :src="post.thumbnail" :alt="post.title" />
    </section>
    <!-- ./Featured Image -->

    <!-- Breadcrumb -->
    <section
      class="px-5 lg:px-0 md:px-0 mb-[38px] w-full md:container overflow-auto whitespace-nowrap breadcrumbs-container border-manatee border-solid hidden md:block"
    >
      <Breadcrumbs :navigation="navigation" />
    </section>
    <!-- ./Breadcrumb -->
    <div class="md:container flex flex-col md:flex-row">
      <article ref="postContent" class="blog-post__content">
        <LazyHydrate>
          <section class="md:container pr-5 lg:px-0 md:px-0 mb-[11px]">
            <!-- Min To Read -->
            <div class="blog-post__min-read">
              <!-- Icon -->
              <IconClock />
              <!-- ./Icon -->

              <!-- Minutes -->
              {{ $i18n('blog.min-read', { minute: post.minRead }) }}
              <!-- ./Minutes -->
            </div>
            <!-- ./Min To Read -->
          </section>
        </LazyHydrate>

        <!-- Post Content -->
        <div class="blog-post__content content pl-5 pr-3 md:px-0 text-sm">
          <h1 v-html="post.title" />
          <div v-html="post.content" />
        </div>
        <!-- Post Content -->

        <div class="blog-post__info">
          <!-- Author -->
          <div
            class="blog-post__info__author"
            v-html="
              $i18n('blog.author-category-date', {
                author: post.author.name,
                category: post.categories ? post.categories[0].name : null,
                date: convertDate(post.datePublished, language.lang),
              })
            "
          />
          <!-- ./Author -->

          <!-- Share Social -->
          <div class="info__social">
            <SocialShare />
          </div>
          <!-- ./Share Social -->
        </div>
      </article>

      <client-only>
        <template v-if="tableContents.length">
          <!-- Progress Bar -->
          <div class="scroll-progressbar hidden md:block">
            <!-- Progress Indicator -->
            <div class="scroll-progressbar__progress-indicator">
              <span class="progress-indicator__count">{{ getReadingProgressPercent }}%</span>
            </div>
            <!-- ./Progress Indicator -->
            <span ref="scrollProgress" class="scroll-progressbar__progress" />
          </div>
          <!-- ./Progress Bar -->

          <!-- Table Of Contents -->
          <div
            ref="tableContentWrapper"
            class="table-content-wrapper"
            :style="!$deviceDetector.isMobile() ? `top:${getTableContentTop}px;` : 'static'"
          >
            <details ref="tableContent" class="table-content" :open="!$deviceDetector.isMobile()">
              <!-- Title -->
              <summary
                class="flex flex-nowrap content-center justify-between items-baseline pr-3 lg:p-0 lg:pointer-events-none"
                @click="tableContentIsActive = !tableContentIsActive"
              >
                <h3 class="table-content__title" v-html="$i18n('blog.table-of-content')" />
                <IconCaretRight
                  :class="[
                    'text-black flex-shrink-0 transition-all duration-200 md:hidden',
                    {
                      'rotate-90': !tableContentIsActive,
                      '-rotate-90': tableContentIsActive,
                    },
                  ]"
                />
              </summary>
              <!-- ./Title -->

              <!-- Blocks -->
              <div class="block">
                <ul>
                  <li
                    v-for="(tableContent, index) in tableContents"
                    :key="`table-content-${index}`"
                    class="flex flex-row items-start gap-[10px] mb-[15px]"
                  >
                    <IconCaretRight class="text-blue-500 flex-shrink-0 mt-[7px]" />
                    <span
                      :class="[
                        'cursor-pointer hover:text-blue-500',
                        { 'text-blue-500': selectedHeading ? selectedHeading.title == tableContent.title : index == 0 },
                      ]"
                      @click="goToHeading(tableContent)"
                      v-html="tableContent.title"
                    >
                    </span>
                  </li>
                </ul>
              </div>
              <!-- ./Blocks -->

              <div class="table-content__progress-percent">
                {{ $i18n('blog.reading-progress', { percent: getReadingProgressPercent }) }}
              </div>
            </details>
          </div>
          <!-- ./Table Of Contents -->
        </template>
      </client-only>
    </div>

    <!-- Email Subscription -->
    <div class="email-subscription">
      <div class="email-subscription__container">
        <EmailSubscription />
      </div>
    </div>
    <!-- ./Email Subscription -->

    <!-- Similar Posts -->
    <div v-if="similarPosts && similarPosts.length" class="similar-posts">
      <!-- Title -->
      <div class="similar-posts__title">
        <h3>{{ $i18n('blog.similar-posts') }}</h3>
      </div>
      <!-- ./Title -->

      <div class="similar-posts-wrapper">
        <PostCard
          v-for="(similarPost, similarPostIndex) in similarPosts"
          :key="`similar-post-${similarPostIndex}`"
          :title="similarPost.title"
          :slug="similarPost.slug"
          :date="similarPost.date"
          :thumbnail="similarPost.thumbnail"
          :categories="similarPost.categories"
          :min-read="similarPost.minRead"
        />
      </div>
    </div>
    <!-- ./Similar Posts -->
  </div>
</template>
<script>
import Breadcrumbs from '~/components/merchant/Breadcrumbs.vue';
import IconClock from '~/components/icons/IconClock.vue';
import IconCaretRight from '~/components/icons/IconCaretRight.vue';
import EmailSubscription from '~/components/blog/subscriptionBar/EmailSubscription.vue';
import PostCard from '~/components/blog/post/PostCard.vue';
import LazyHydrate from 'vue-lazy-hydration';
import moment from 'moment';
import SocialShare from './components/SocialShare.vue';

const DatePicker = () => import('~/components/experiments/tr-841-improved-date-picker/index.vue');

export default {
  name: 'Post',
  components: {
    Breadcrumbs,
    IconClock,
    IconCaretRight,
    EmailSubscription,
    PostCard,
    SocialShare,
    LazyHydrate,
    DatePicker,
  },
  data() {
    return {
      readingProgress: {
        distance: 0,
        percent: 0,
      },
      selectedHeading: null,
      tableContents: [],
      tableContentTop: 0,
      tableContentMaxHeight: 0,
      tableContentHeight: 0,
      tableContentWrapper: 0,
      tableContentIsActive: false,
    };
  },
  head() {
    const { slug, meta, thumbnail, author, datePublished, dateModified, tags } = this.post;

    let canonical = this.$paths.url(false);

    if (this.getCurrentPage > 1) {
      canonical += this.$router.resolve({
        name: 'blog-value',
        params: {
          value: slug,
        },
      }).href;
    } else {
      canonical += this.$route.path;
    }

    /* eslint-disable */
    const alternates = this.post?.hreflangs
      .filter((m) => {
        // Only include hreflangs that have a corresponding language
        return this.languages.some((l) => l.lang === m.lang);
      })
      .map((m) => {
        const language = this.languages.find((l) => l.lang === m.lang);

        if (language) {
          return {
            rel: 'alternate',
            hreflang: m.lang === 'en-eu' ? 'en' : m.lang,
            href: `https://${language.domain}/blog/${m.slug}/`,
          };
        }
      });
    /* eslint-enable */
    const link = [{ rel: 'canonical', href: canonical }];
    link.push(...alternates);
    return {
      title: meta.title,
      htmlAttrs: {
        lang: this.getHrefLang,
      },
      meta: [
        { 'http-equiv': 'content-language', content: this.getHrefLang },
        {
          property: 'og:title',
          content: meta.title,
        },
        {
          name: 'description',
          content: meta.description,
        },
        {
          property: 'og:description',
          content: meta.description,
        },
        { name: 'twitter:card', content: 'summary' },
        {
          name: 'twitter:title',
          content: meta.title,
        },
        {
          name: 'twitter:site',
          content: this.language.socials.twitter ? `@${this.language.socials.twitter.split('/').pop()}` : '',
        },
        {
          name: 'twitter:creator',
          content: this.language.socials.twitter ? `@${this.language.socials.twitter.split('/').pop()}` : '',
        },
        {
          name: 'twitter:description',
          content: meta.description,
        },
        {
          name: 'twitter:image',
          content: thumbnail,
        },
        { property: 'og:type', content: 'article' },
        { property: 'og:locale', content: this.getHrefLang },
        {
          property: 'og:image',
          content: thumbnail,
        },
        {
          property: 'og:url',
          content: `${this.$paths.url(false)}${
            this.$route.path[this.$route.path.length - 1] === '/'
              ? `${this.$route.path.substring(0, this.$route.path.length - 1)}/`
              : `${this.$route.path}/`
          }`,
        },
        { property: 'article:author', content: author.name },
        { property: 'article:published_time', content: datePublished },
        { property: 'article:modified_time', content: dateModified },
        { property: 'article:tag', content: tags?.join(',') },
      ],
      link,
    };
  },
  jsonld() {
    const { author, meta, thumbnail, datePublished, dateModified } = this.post;

    return {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      headline: meta.title,
      image: [thumbnail],
      datePublished,
      dateModified,
      author: [
        {
          '@type': 'Person',
          name: author?.name,
        },
      ],
      '@graph': [
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              item: {
                '@id': `${this.$paths.url(false)}/blog/`,
                name: 'Blog',
              },
            },
            {
              '@type': 'ListItem',
              position: 2,
              item: {
                '@id': `${this.$paths.url(false) + this.$route.path}`,
                name: meta.title,
              },
            },
          ],
        },
      ],
    };
  },
  computed: {
    navigation() {
      return [
        {
          href: '/',
          title: 'Home',
        },
        {
          href: '/blog/',
          title: 'Blog',
        },
        {
          href: `/blog/${this.post.slug}/`,
          title: this.post.title,
        },
      ];
    },
    getReadingProgressPercent() {
      return this.readingProgress.percent.toFixed(0);
    },
    post() {
      return this.$store.state.post.post;
    },
    similarPosts() {
      return this.$store.state.post.similarPosts;
    },
    language() {
      return this.$store.state?.language;
    },
    languages() {
      return this.$store.state.languages;
    },
    getCurrentPage() {
      return parseInt(this.$route?.query?.page || 1, 10);
    },
    getTableContentTop() {
      if (Number(this.tableContentTop).toFixed(2) >= Number(0)) {
        return 0;
      }
      if (Number(this.tableContentTop.toFixed(2)) * -1 < this.tableContentMaxHeight) {
        return Number(this.tableContentTop.toFixed(2)) * -1;
      }
      return Number(this.tableContentMaxHeight) + 28;
    },
    getHrefLang() {
      return this.language.lang === 'en-eu' ? 'en' : this.language.lang;
    },
    maintitle() {
      const { airport_slug: airportSlug } = this.post.acf;
      let airport;

      if (airportSlug && airportSlug?.length) {
        airport = this.$store.state.airports.find((airport) => airport.slug === airportSlug);
      }

      return airport ? airport.maintitle : this.$i18n('general.compare-parking');
    },
  },
  watch: {
    readingProgress: {
      handler(scroll) {
        this.$refs.scrollProgress.style.height = `${scroll.distance}px`;
      },
      deep: true,
    },
  },
  mounted() {
    document.addEventListener('scroll', this.handleScroll);
    this.setTableContents();
  },
  methods: {
    handleScroll() {
      let distance = 0;
      let percent = 0;

      const post = this.$refs.postContent;

      if (post) {
        const postHeight = post.getBoundingClientRect().height;
        this.tableContentWrapper = this.$refs.tableContentWrapper.getBoundingClientRect().height;
        this.tableContentTop = post.getBoundingClientRect().top;
        this.tableContentHeight = this.$refs.tableContent.getBoundingClientRect().height;
        this.tableContentMaxHeight = postHeight - this.tableContentHeight;
        const postOffsetTop = post.offsetTop - 300;
        const windowScrollTop = window.scrollY + 400;

        if (windowScrollTop >= postOffsetTop) {
          if (windowScrollTop <= postOffsetTop + postHeight) {
            distance = windowScrollTop - postOffsetTop;
            percent = 100 * (distance / postHeight);
          } else {
            percent = 100;
            distance = postHeight + 80;
          }
        }

        this.readingProgress.percent = percent;
        this.readingProgress.distance = distance;

        this.setSelectedHeading();
      }
    },
    setTableContents() {
      const article = document.querySelector('article .blog-post__content');
      const headings = article.querySelectorAll('h1, h2, h3, h4, h5, h6');
      if (headings && headings.length) {
        headings.forEach((heading) => {
          const headingData = {
            title: heading.textContent,
            offsetTop: heading.offsetTop,
          };
          this.tableContents.push(headingData);
        });
      }
    },
    setSelectedHeading() {
      if (this.tableContents.length) {
        this.tableContents.forEach((content) => {
          if (content.offsetTop <= window.scrollY) {
            this.selectedHeading = content;
          }
        });
      }
    },
    goToHeading(content) {
      window.scrollTo(0, content.offsetTop);
    },
    convertDate(val, lang) {
      const date = new Date(val);
      return moment(date).locale(lang).format('MMMM, YYYY');
    },
  },
};
</script>
<style lang="scss">
.blog-post {

  .wp-block-list{
    margin-bottom:30px;
  }
  .wp-block-details{
    summary{
      @apply font-bold cursor-pointer;
      &+p{
        margin-bottom:25px;
      }
    }
  }


  &__featured-image {
    & img {
      @apply block relative w-full max-h-[400px] object-cover object-center overflow-hidden;
    }
  }

  &__min-read {
    @apply flex flex-row items-center gap-10px text-gray-400;
  }

  &__content {
    @apply w-full lg:w-9/12 mb-[80px] pl-5 pr-5 lg:pl-0 md:pl-0 md:pr-0 lg:pr-[62px] order-2 md:order-1;

    &.content {
      @apply w-full text-[18px] mb-[20px] pb-[30px] border-b border-gray-200 leading-9 pl-0;

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        @apply font-cocogoose mb-[10px];
      }
      h1 {
        @apply text-[28px] leading-relaxed;
      }
      h2 {
        @apply leading-tight text-[26px];
      }
      h3 {
        @apply leading-tight text-[22px];
      }
      h4 {
        @apply leading-tight text-[20px];
      }

      p:not(:last-child),
      figure {
        @apply mb-[30px];
      }
      a {
        @apply text-blue-500 hover:underline;
      }
    }
    & .has-background {
      padding: 12px 16px;
    }
    & ul {
      list-style: disc;
      padding-left: revert;
      li {
        strong {
          @apply text-base;
        }
      }
    }
    & ol {
      list-style: decimal;
      padding-left: 1.25rem;
    }
    & a {
      @apply text-blue-500 hover:underline;
    }
  }

  &__info {
    @apply flex md:flex-row flex-col items-start md:items-center justify-between pr-5 lg:px-0;

    &__author {
      @apply text-[#9797a6];
      & > strong {
        @apply text-[#0e1017] font-normal;
      }
    }
  }
}

.table-content {
  @apply w-full relative;

  &__title {
    @apply font-cocogoose py-5 lg:py-0 lg:mb-3.75 text-[20px];
  }

  &__progress-percent {
    @apply font-extrabold text-gray-400 text-lg hidden lg:block;
  }
  &-wrapper {
    @apply h-fit p-5 py-0 bg-blue-50 lg:bg-[#f4f7fe] rounded-tr flex relative mb-10 order-1 lg:order-3 lg:mb-0 lg:p-7;
    width: 100%;
    @media (min-width: 1024px) {
      max-width: 340px;
    }
  }
}

.scroll-progressbar {
  @apply w-2/12 w-1 bg-[#f4f7fe] order-2;

  &__progress {
    @apply lg:w-full block bg-blue-500;

    &-indicator {
      @apply sticky top-[60px];
    }
  }

  .progress-indicator__count {
    @apply absolute bg-blue-500 text-white rounded-r-[4px] text-[14px] py-1 px-2 block lg:hidden;
  }
}

.email-subscription {
  @apply flex flex-row bg-[#f4f7fe];

  &__container {
    @apply px-5 lg:px-0 md:container py-[60px] content-center items-center;
  }
}

.similar-posts {
  @apply md:container px-5 lg:px-0 md:px-0 mb-[30px] mt-[80px];

  &__title {
    @apply font-cocogoose text-[28px] mb-[30px];
  }

  &-wrapper {
    @apply grid grid-cols-6 gap-x-[30px];

    .article-card {
      @apply lg:col-span-2 col-span-full;
    }
  }
}
.footer {
  @apply top-[4.7rem] relative md:top-auto;
}
</style>
